
import { logger } from "@/utils/logger";
import { Options, Vue } from "vue-class-component";
import Notify from 'quasar/src/plugins/Notify.js';;
import moment from "moment";
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import AnimatedCounter from "@/components/home/AnimatedCounter.vue";
import router from "@/router";
import store from "@/store";
import { fetchDashboardMetrics } from "@/store/services/homeDashboardService";
import {
  buildDashboardMetrics,
  computePercentChange,
  DashboardMetrics,
  formatChannelLabel as formatChannelLabelValue,
  formatDashboardAmount,
  formatTrendLabel,
  getChannelStatusTotal,
  getDashboardPeriodDates,
  getPreviousDashboardPeriodDates,
  getStatusSlicePercent,
  getTopChannelEntries,
} from "@/utils/homeDashboardMetrics";
import {
  DashboardDemandFilterPreset,
  saveDashboardDemandFilter,
} from "@/utils/homeDashboardNavigation";
import { addDaysToStringDate } from "@/utils/configuration/formatters-config";
import { OfferSearch } from "@/types";

Chart.register(
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  DoughnutController,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip
);

const EMPTY_METRICS = buildDashboardMetrics([]);

interface KpiTrends {
  totalSimulations: number | null;
  physicalPersons: number | null;
  legalEntities: number | null;
  totalAmount: number | null;
}

const EMPTY_TRENDS: KpiTrends = {
  totalSimulations: null,
  physicalPersons: null,
  legalEntities: null,
  totalAmount: null,
};

@Options({
  components: { AnimatedCounter },
})
export default class Home extends Vue {
  name = "Home-Teamwill";

  isLoading = true;
  selectedPeriod = 30;
  metrics: DashboardMetrics = EMPTY_METRICS;
  kpiTrends: KpiTrends = { ...EMPTY_TRENDS };
  lastUpdated: Date | null = null;

  periodOptions = [
    { label: "30 derniers jours", value: 30 },
    { label: "90 derniers jours", value: 90 },
    { label: "12 derniers mois", value: 365 },
    { label: "Tout", value: 0 },
  ];

  private channelCharts = new Map<string, Chart>();
  private barChart: Chart | null = null;
  private lineChart: Chart | null = null;

  get userFirstName(): string {
    return store.state.authModule?.user?.firstName?.trim() || "";
  }

  get periodLabel(): string {
    return (
      this.periodOptions.find((option) => option.value === this.selectedPeriod)?.label ||
      ""
    );
  }

  get welcomeTitle(): string {
    if (this.userFirstName) {
      return `Bonjour ${this.userFirstName}`;
    }
    return String(this.$t("main.title"));
  }

  get welcomeSubtitle(): string {
    const base = String(this.$t("main.subtitle"));
    if (this.selectedPeriod === 0) {
      return `${base} — toutes les périodes`;
    }
    return `${base} — ${this.periodLabel.toLowerCase()}`;
  }

  get lastUpdatedLabel(): string {
    if (!this.lastUpdated) return "";
    return `Mis à jour à ${moment(this.lastUpdated).format("HH:mm")}`;
  }

  get channelChartColClass(): string {
    const count = this.metrics.statusByChannel.length;
    if (count <= 1) return "col-lg-6 col-md-12 col-sm-12";
    if (count === 2) return "col-lg-4 col-md-12 col-sm-12";
    return "col-lg-3 col-md-6 col-sm-12";
  }

  get financingChartColClass(): string {
    const count = this.metrics.statusByChannel.length;
    if (count === 0) return "col-12";
    if (count === 1) return "col-lg-6 col-md-12 col-sm-12";
    if (count === 2) return "col-lg-4 col-md-12 col-sm-12";
    return "col-lg-3 col-md-6 col-sm-12";
  }

  formatAmount(value: number): string {
    return formatDashboardAmount(value);
  }

  formatChannelLabel(channel: string): string {
    return formatChannelLabelValue(channel);
  }

  formatTrend(change: number | null): string {
    return formatTrendLabel(change);
  }

  getTopChannelEntries(byChannel: Record<string, number>) {
    return getTopChannelEntries(byChannel);
  }

  getChannelStatusTotal(
    channelStatus: DashboardMetrics["statusByChannel"][number]
  ): number {
    return getChannelStatusTotal(channelStatus);
  }

  getStatusPercent(
    count: number,
    channelStatus: DashboardMetrics["statusByChannel"][number]
  ): number {
    return getStatusSlicePercent(count, this.getChannelStatusTotal(channelStatus));
  }

  mounted() {
    this.loadDashboard();
  }

  beforeUnmount() {
    this.destroyCharts();
  }

  buildPeriodFilter(days: number): OfferSearch {
    if (days <= 0) return {};
    const { from, until } = getDashboardPeriodDates(days);
    return {
      from,
      until: addDaysToStringDate(until, 1),
    };
  }

  buildPreviousPeriodFilter(days: number): OfferSearch {
    const { from, until } = getPreviousDashboardPeriodDates(days);
    return {
      from,
      until: addDaysToStringDate(until, 1),
    };
  }

  async loadDashboard() {
    this.isLoading = true;
    try {
      const currentFilter = this.buildPeriodFilter(this.selectedPeriod);
      const previousFilter =
        this.selectedPeriod > 0
          ? this.buildPreviousPeriodFilter(this.selectedPeriod)
          : null;

      const [currentMetrics, previousMetrics] = await Promise.all([
        fetchDashboardMetrics(currentFilter),
        previousFilter
          ? fetchDashboardMetrics(previousFilter)
          : Promise.resolve(null),
      ]);

      this.metrics = currentMetrics;
      this.kpiTrends = previousMetrics
        ? {
            totalSimulations: computePercentChange(
              currentMetrics.totalSimulations.total,
              previousMetrics.totalSimulations.total
            ),
            physicalPersons: computePercentChange(
              currentMetrics.physicalPersons.total,
              previousMetrics.physicalPersons.total
            ),
            legalEntities: computePercentChange(
              currentMetrics.legalEntities.total,
              previousMetrics.legalEntities.total
            ),
            totalAmount: computePercentChange(
              currentMetrics.totalAmount.total,
              previousMetrics.totalAmount.total
            ),
          }
        : { ...EMPTY_TRENDS };

      this.lastUpdated = new Date();
      this.isLoading = false;
      await this.$nextTick();
      this.renderCharts();
    } catch (error) {
      logger.error(error);
      this.metrics = EMPTY_METRICS;
      this.kpiTrends = { ...EMPTY_TRENDS };
      this.isLoading = false;
      Notify.create({
        timeout: 10000,
        actions: [{ icon: "close", color: "white" }],
        message: "Erreur lors du chargement du dashboard",
        color: "negative",
      });
    }
  }

  navigateToDemands(preset: DashboardDemandFilterPreset) {
    saveDashboardDemandFilter({
      ...this.buildPeriodFilter(this.selectedPeriod),
      ...preset,
    });
    router.push("/demand/list");
  }

  goToTasks() {
    router.push("/task/list");
  }

  goToDemandSearch() {
    this.navigateToDemands({});
  }

  destroyCharts() {
    this.channelCharts.forEach((chart) => chart.destroy());
    this.channelCharts.clear();
    this.barChart?.destroy();
    this.lineChart?.destroy();
    this.barChart = null;
    this.lineChart = null;
  }

  renderCharts() {
    this.destroyCharts();
    this.renderChannelDoughnuts();
    this.renderFinancingBar();
    this.renderMonthlyLine();
  }

  renderChannelDoughnuts() {
    this.metrics.statusByChannel.forEach((channelData) => {
      if (!channelData.slices.length) return;

      const canvas = (this.$el as HTMLElement | undefined)?.querySelector(
        `canvas.home-channel-chart[data-channel="${channelData.channel}"]`
      ) as HTMLCanvasElement | null;

      if (!canvas) return;

      const total = this.getChannelStatusTotal(channelData);
      const chart = new Chart(canvas, {
        type: "doughnut",
        data: {
          labels: channelData.slices.map((item) => item.label),
          datasets: [
            {
              data: channelData.slices.map((item) => item.count),
              backgroundColor: channelData.slices.map((item) => item.color),
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "68%",
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              callbacks: {
                label: (context:any) => {
                  const count = Number(context.parsed) || 0;
                  const percent = getStatusSlicePercent(count, total);
                  return `${context.label}: ${count} (${percent}%)`;
                },
              },
            },
          },
        },
      });

      this.channelCharts.set(channelData.channel, chart);
    });
  }

  renderFinancingBar() {
    const canvas = this.$refs.financingBarChart as HTMLCanvasElement | undefined;
    if (!canvas) return;

    this.barChart = new Chart(canvas, {
      type: "bar",
      data: {
        labels: this.metrics.financingTypes.map((item) => item.label),
        datasets: [
          {
            data: this.metrics.financingTypes.map((item) => item.count),
            backgroundColor: this.metrics.financingTypes.map((item) => item.color),
            borderRadius: 8,
            maxBarThickness: 72,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (context:any) => {
                const item = this.metrics.financingTypes[context.dataIndex];
                if (!item) return "";
                return [
                  `${item.count} simulation(s)`,
                  `Montant: ${this.formatAmount(item.amount)}`,
                ];
              },
            },
          },
        },
        scales: {
          x: { grid: { display: false } },
          y: { beginAtZero: true, ticks: { precision: 0 } },
        },
      },
    });
  }

  renderMonthlyLine() {
    const canvas = this.$refs.monthlyLineChart as HTMLCanvasElement | undefined;
    if (!canvas) return;

    this.lineChart = new Chart(canvas, {
      type: "line",
      data: {
        labels: this.metrics.monthlyEvolution.map((item) => item.month),
        datasets: this.metrics.monthlySeries.map((series) => ({
          label: series.label,
          data: this.metrics.monthlyEvolution.map(
            (item) => item.values[series.key] || 0
          ),
          borderColor: series.color,
          backgroundColor: `${series.color}1f`,
          tension: 0.35,
          fill: true,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "bottom" },
          tooltip: {
            callbacks: {
              label: (context:any) => {
                const series = this.metrics.monthlySeries[context.datasetIndex];
                const point = this.metrics.monthlyEvolution[context.dataIndex];
                if (!series || !point) return "";
                const count = point.values[series.key] || 0;
                const amount = point.amounts[series.key] || 0;
                return [
                  `${series.label}: ${count} offre(s)`,
                  `Montant: ${this.formatAmount(amount)}`,
                ];
              },
            },
          },
        },
        scales: {
          y: { beginAtZero: true, ticks: { precision: 0 } },
        },
      },
    });
  }
}
